// Accounting summary and analytics hooks
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { queryKeys } from '@/lib/query-client'
import type {
  ExpenseCategory,
  FinancialSummary,
  ExpenseBreakdownItem,
  SalarySummary,
  RecurringExpensesSummary,
} from '@/types/entities'

// ============================================
// EXPENSE CATEGORIES HOOKS
// ============================================

/**
 * Fetch expense categories for a restaurant (auto-seeds if needed)
 */
export function useExpenseCategories(restaurantId: string | null) {
  return useQuery({
    queryKey: queryKeys.accounting.expenseCategories(restaurantId || ''),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async (): Promise<ExpenseCategory[]> => {
      if (!restaurantId) {
        return []
      }

      // First, check and seed default categories if needed
      await supabase.rpc('check_and_seed_expense_categories', {
        p_restaurant_id: restaurantId,
      })

      // Then fetch all categories
      const { data, error } = await supabase.rpc('get_expense_categories', {
        p_restaurant_id: restaurantId,
      })

      if (error) {
        throw error
      }

      return (data || []).map((ec: any) => ({
        id: ec.id,
        restaurantId: ec.restaurant_id,
        nameAr: ec.name_ar,
        nameFr: ec.name_fr,
        icon: ec.icon,
        color: ec.color,
        isSystem: ec.is_system,
        isActive: ec.is_active,
        sortOrder: ec.sort_order,
        createdAt: ec.created_at,
        updatedAt: ec.updated_at,
      }))
    },
    enabled: !!restaurantId,
  })
}

/**
 * Create a new expense category
 */
export function useCreateExpenseCategory(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      nameAr: string
      nameFr: string
      icon?: string
      color?: string
    }) => {
      const { data, error } = await supabase.rpc('create_expense_category', {
        p_restaurant_id: restaurantId,
        p_name_ar: request.nameAr,
        p_name_fr: request.nameFr,
        p_icon: request.icon || 'receipt',
        p_color: request.color || '#6b7280',
      })

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounting.expenseCategories(restaurantId),
      })
    },
  })
}

/**
 * Update an expense category
 */
export function useUpdateExpenseCategory(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      categoryId: string
      nameAr: string
      nameFr: string
      icon: string
      color: string
      isActive: boolean
    }) => {
      const { data, error } = await supabase.rpc('update_expense_category', {
        p_category_id: request.categoryId,
        p_name_ar: request.nameAr,
        p_name_fr: request.nameFr,
        p_icon: request.icon,
        p_color: request.color,
        p_is_active: request.isActive,
      })

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounting.expenseCategories(restaurantId),
      })
    },
  })
}

/**
 * Delete an expense category (non-system only)
 */
export function useDeleteExpenseCategory(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (categoryId: string) => {
      const { data, error } = await supabase.rpc('delete_expense_category', {
        p_category_id: categoryId,
      })

      if (error) {
        // Check if it's a foreign key constraint error
        if (error.code === '23503') {
          throw new Error('CATEGORY_IN_USE')
        }
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounting.expenseCategories(restaurantId),
      })
    },
  })
}

// ============================================
// FINANCIAL SUMMARY HOOKS
// ============================================

/**
 * Fetch financial summary (P&L) for a period
 */
export function useFinancialSummary(
  restaurantId: string | null,
  startDate: string,
  endDate: string
) {
  return useQuery({
    queryKey: queryKeys.accounting.financialSummary(restaurantId || '', startDate, endDate),
    staleTime: 2 * 60 * 1000, // 2 minutes for more frequent updates
    gcTime: 5 * 60 * 1000,
    queryFn: async (): Promise<FinancialSummary | null> => {
      if (!restaurantId) {
        return null
      }

      const { data, error } = await supabase.rpc('get_profit_loss_for_period', {
        p_restaurant_id: restaurantId,
        p_start_date: startDate,
        p_end_date: endDate,
      })

      if (error) {
        throw error
      }

      if (!data || data.length === 0) {
        return {
          totalRevenue: 0,
          totalCogs: 0,
          grossProfit: 0,
          totalExpenses: 0,
          netProfit: 0,
          profitMargin: 0,
          totalOrders: 0,
          expenseCount: 0,
        }
      }

      const row = data[0]
      if (!row) {
        return {
          totalRevenue: 0,
          totalCogs: 0,
          grossProfit: 0,
          totalExpenses: 0,
          netProfit: 0,
          profitMargin: 0,
          totalOrders: 0,
          expenseCount: 0,
        }
      }
      return {
        totalRevenue: Number(row.total_revenue),
        totalCogs: Number(row.total_cogs),
        grossProfit: Number(row.gross_profit),
        totalExpenses: Number(row.total_expenses),
        netProfit: Number(row.net_profit),
        profitMargin: Number(row.profit_margin),
        totalOrders: Number(row.total_orders),
        expenseCount: Number(row.expense_count),
      }
    },
    enabled: !!restaurantId && !!startDate && !!endDate,
  })
}

/**
 * Fetch expense breakdown by category for a period
 */
export function useExpenseBreakdown(
  restaurantId: string | null,
  startDate: string,
  endDate: string
) {
  return useQuery({
    queryKey: queryKeys.accounting.expenseBreakdown(restaurantId || '', startDate, endDate),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ExpenseBreakdownItem[]> => {
      if (!restaurantId) {
        return []
      }

      const { data, error } = await supabase.rpc('get_expense_breakdown_by_category', {
        p_restaurant_id: restaurantId,
        p_start_date: startDate,
        p_end_date: endDate,
      })

      if (error) {
        throw error
      }

      return (data || []).map((item: any) => ({
        categoryId: item.category_id,
        categoryNameAr: item.category_name_ar,
        categoryNameFr: item.category_name_fr,
        categoryIcon: item.category_icon,
        categoryColor: item.category_color,
        totalAmount: Number(item.total_amount),
        expenseCount: Number(item.expense_count),
        percentage: Number(item.percentage),
      }))
    },
    enabled: !!restaurantId && !!startDate && !!endDate,
  })
}

/**
 * Fetch salary summary (total monthly salaries)
 */
export function useSalarySummary(restaurantId: string | null) {
  return useQuery({
    queryKey: queryKeys.accounting.salarySummary(restaurantId || ''),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async (): Promise<SalarySummary | null> => {
      if (!restaurantId) {
        return null
      }

      const { data, error } = await supabase.rpc('get_monthly_salary_summary', {
        p_restaurant_id: restaurantId,
      })

      if (error) {
        throw error
      }

      if (!data || data.length === 0) {
        return { totalMonthlySalary: 0, activeEmployees: 0 }
      }

      const row = data[0]
      if (!row) {
        return { totalMonthlySalary: 0, activeEmployees: 0 }
      }
      return {
        totalMonthlySalary: Number(row.total_monthly_salary),
        activeEmployees: Number(row.active_employees),
      }
    },
    enabled: !!restaurantId,
  })
}

/**
 * Fetch recurring expenses summary (total monthly commitment)
 */
export function useRecurringExpensesSummary(restaurantId: string | null) {
  return useQuery({
    queryKey: queryKeys.accounting.recurringExpensesSummary(restaurantId || ''),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async (): Promise<RecurringExpensesSummary | null> => {
      if (!restaurantId) {
        return null
      }

      const { data, error } = await supabase.rpc('get_recurring_expenses_summary', {
        p_restaurant_id: restaurantId,
      })

      if (error) {
        throw error
      }

      if (!data || data.length === 0) {
        return { totalMonthlyRecurring: 0, activeRecurringCount: 0 }
      }

      const row = data[0]
      if (!row) {
        return { totalMonthlyRecurring: 0, activeRecurringCount: 0 }
      }
      return {
        totalMonthlyRecurring: Number(row.total_monthly_recurring),
        activeRecurringCount: Number(row.active_recurring_count),
      }
    },
    enabled: !!restaurantId,
  })
}
